import {
  Box,
  Button,
  Card,
  CloseButton,
  chakra,
  Dialog,
  Heading,
  HStack,
  Image,
  Portal,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Binary,
  Boxes,
  Check,
  FileJson,
  GitFork,
  LayoutDashboard,
  type LucideIcon,
  MemoryStick,
  MonitorCog,
  PackageSearch,
  Puzzle,
  Settings,
  Shield,
  ZoomIn,
} from 'lucide-react'
import { useState } from 'react'

import datahubEngine from '@/assets/gifs/datahubEngine.gif'
import { cardHover, InsightCard, PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/datahub')({
  component: DataHubPage,
})

interface ZoomableMediaProps {
  src: string
  alt: string
  maxH?: string
  onZoom: (src: string, alt: string) => void
}

const ZoomTrigger = chakra('button')

function ZoomableMedia({ src, alt, maxH = '360px', onZoom }: ZoomableMediaProps) {
  return (
    <ZoomTrigger
      type="button"
      position="relative"
      cursor="zoom-in"
      onClick={() => onZoom(src, alt)}
      aria-label={`Zoom ${alt}`}
      bg="transparent"
      border="none"
      p={0}
      _hover={{
        '& > .zoom-overlay': {
          opacity: 1,
        },
      }}
    >
      <Image src={src} alt={alt} maxH={maxH} objectFit="contain" borderRadius="lg" />
      <Box
        className="zoom-overlay"
        position="absolute"
        top={2}
        right={2}
        bg="blackAlpha.600"
        borderRadius="md"
        p={2}
        opacity={0}
        transition="opacity 0.2s"
      >
        <ZoomIn size={20} color="white" />
      </Box>
    </ZoomTrigger>
  )
}

interface FeatureItemProps {
  title: string
  description: string
}

function FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <HStack gap={3} align="flex-start">
      <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
        <Check size={14} color="var(--chakra-colors-green-600)" />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </VStack>
    </HStack>
  )
}

interface FeatureCardProps {
  icon: typeof Shield
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root h="100%">
      <Card.Body p={5}>
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface FlowCardProps {
  title: string
  description: string
  route: string
}

function FlowCard({ title, description, route }: FlowCardProps) {
  const navigate = useNavigate()

  return (
    <Card.Root
      overflow="hidden"
      transition="all 0.3s"
      _hover={cardHover}
      cursor="pointer"
      onClick={() => navigate({ to: route })}
      h="100%"
    >
      <Card.Header>
        <Heading size="sm">{title}</Heading>
      </Card.Header>
      <Card.Body>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </Card.Body>
      <Card.Footer>
        <Button
          w="100%"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            navigate({ to: route })
          }}
        >
          View Flow
          <ArrowRight size={14} />
        </Button>
      </Card.Footer>
    </Card.Root>
  )
}

const tags = ['Data Quality', 'Schema Validation', 'Transformation', 'Governance']

const features: FeatureCardProps[] = [
  {
    icon: Shield,
    title: 'Data Policies',
    description:
      'Define set of rules and guidelines to enforce how data and messages should be expected.',
  },
  {
    icon: Settings,
    title: 'Data Validation & Transformation',
    description: 'Defining and enforcing data standards across deployments.',
  },
  {
    icon: FileJson,
    title: 'Data Schemas',
    description:
      'Create the blueprint for how data is formatted. JSON and Protobuf currently supported.',
  },
  {
    icon: AlertTriangle,
    title: 'Policy Actions',
    description:
      'Describe what should happen to messages/data that fail validation. Messages can be rerouted, forwarded, or simply logged.',
  },
  {
    icon: LayoutDashboard,
    title: 'Control Center',
    description:
      'Simple GUI to manage schemas, data and behavior policies with quality metrics dashboard.',
  },
  {
    icon: Boxes,
    title: 'Modules',
    description:
      'Pre-defined modules that implement ready-to-use functionalities accessible from Control Center.',
  },
]

interface NewCapabilityProps {
  accent: string
  colorPalette: string
  icon: LucideIcon
  title: string
  description: string
}

const newCapabilities: NewCapabilityProps[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: GitFork,
    title: 'Fan-Out Transformations',
    description:
      'Create multiple MQTT messages from a single incoming message. Ideal for splitting Sparkplug metrics into individual subtopics or routing data to multiple consumers.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: MemoryStick,
    title: 'Stateful Transformations',
    description:
      'JavaScript scripts maintain per-client-connection state (rolling averages, counters, deduplication) without any external storage.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: Binary,
    title: 'Protobuf Schema Support',
    description:
      'Google Protocol Buffers supported natively throughout the pipeline. Data stays in Protobuf without forced JSON conversion, preserving efficiency and schema fidelity.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: Puzzle,
    title: 'Pre-Built Module Ecosystem',
    description:
      'One-click installable modules (Sparkplug→JSON fan-out, duplicate-message filter, JSON schema validator), configurable directly from Control Center - no code.',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: PackageSearch,
    title: 'Custom Module Registry',
    description:
      'Host your own module registry on any HTTP server. Publish, version, and share modules across deployments, with full CRUD via REST API.',
  },
  {
    accent: 'cyan.solid',
    colorPalette: 'cyan',
    icon: MonitorCog,
    title: 'Control Center v2 Management',
    description:
      'Data Hub schemas, scripts, policies, and audit log all managed from the new Control Center v2 interface.',
  },
]

const flows: FlowCardProps[] = [
  {
    title: 'Anonymization Flow',
    description: 'Data anonymization and privacy protection flows.',
    route: '/dhanonymizeflow',
  },
  {
    title: 'F to C Flow',
    description: 'Fahrenheit to Celsius conversion flow.',
    route: '/dhftocflow',
  },
  {
    title: 'Geolocation Flow',
    description: 'Geolocation data enrichment flow.',
    route: '/dhgeolocationflow',
  },
  {
    title: 'Sparkplug Fanout',
    description: 'Sparkplug message fanout flow.',
    route: '/dhsparkplugfanoutflow',
  },
]

function DataHubPage() {
  const navigate = useNavigate()
  const [zoomMedia, setZoomMedia] = useState<{ src: string; alt: string } | null>(null)

  const handleZoom = (src: string, alt: string) => {
    setZoomMedia({ src, alt })
  }

  const handleCloseZoom = () => {
    setZoomMedia(null)
  }

  return (
    <Box maxW="1400px" mx="auto">
      <Button variant="ghost" mb={4} onClick={() => navigate({ to: '/hivemqpilars' })}>
        <ArrowLeft size={18} />
        Back to Pillars
      </Button>

      <PageHeader
        title="HiveMQ DataHub"
        subtitle="Ensure data quality at the source with powerful validation, transformation, and governance capabilities."
      />

      <VStack align="stretch" gap={10}>
        {/* Overview: capability tags + engine animation */}
        <VStack gap={6}>
          <HStack gap={2} flexWrap="wrap" justify="center">
            {tags.map((tag) => (
              <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                  {tag}
                </Text>
              </Box>
            ))}
          </HStack>

          <Card.Root overflow="hidden" w="100%">
            <Card.Body p={6} display="flex" justifyContent="center">
              <ZoomableMedia
                src={datahubEngine}
                alt="DataHub Engine"
                maxH="360px"
                onZoom={handleZoom}
              />
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Why DataHub */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Why DataHub"
            description="Poor data quality leads to poor decisions. DataHub ensures your MQTT data is clean, validated, and properly formatted before it reaches your applications and AI models."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* How It Works */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Data Quality Pipeline"
            description="DataHub processes messages in real-time as they flow through HiveMQ"
          />
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} alignItems="stretch">
            <Card.Root>
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <FeatureItem
                    title="1. Receive Messages"
                    description="Messages arrive from connected devices via MQTT protocol."
                  />
                  <FeatureItem
                    title="2. Schema Validation"
                    description="Validate message format against JSON or Protobuf schemas."
                  />
                  <FeatureItem
                    title="3. Apply Transformations"
                    description="Transform, enrich, or normalize data as needed."
                  />
                  <FeatureItem
                    title="4. Execute Policy Actions"
                    description="Route valid messages or handle failures according to policies."
                  />
                  <FeatureItem
                    title="5. Monitor Quality"
                    description="Track data quality metrics and identify bad data sources."
                  />
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root bg="bg.subtle">
              <Card.Body p={6}>
                <VStack gap={4}>
                  <Heading size="md">Key Benefits</Heading>
                  <VStack align="start" gap={3} w="100%">
                    {[
                      'Ensure data consistency across all sources',
                      'Reduce downstream errors and failed analytics',
                      'Identify and isolate bad data sources quickly',
                      'Prepare data for AI/ML model training',
                      'Meet compliance and governance requirements',
                    ].map((benefit) => (
                      <HStack key={benefit} gap={3}>
                        <Box p={1} bg="green.100" borderRadius="full">
                          <Check size={14} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">{benefit}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* What Data Hub Can Do */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="What Data Hub Can Do"
            description="Govern, transform, and enrich data streams at the broker layer - without moving data out of the network."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {newCapabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="Capability"
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Interactive Flows */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="DataHub Use Cases"
            description="Explore interactive data flow visualizations demonstrating DataHub capabilities"
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap={6}>
            {flows.map((flow) => (
              <FlowCard key={flow.title} {...flow} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Ready to explore more?</Heading>
                <Text color="content.secondary">
                  Discover how HiveMQ Edge brings connectivity to the edge
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/hivemqedge' })}>
                Explore HiveMQ Edge
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>

      {/* Zoom Modal */}
      <Dialog.Root
        open={zoomMedia !== null}
        onOpenChange={(details) => {
          if (!details.open) {
            handleCloseZoom()
          }
        }}
        size="cover"
        placement="center"
        motionPreset="scale"
      >
        <Portal>
          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content
              bg="transparent"
              shadow="none"
              maxW="95vw"
              maxH="95vh"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Dialog.CloseTrigger asChild position="absolute" top={4} right={4}>
                <CloseButton
                  aria-label="Close zoomed image"
                  size="lg"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  borderRadius="full"
                />
              </Dialog.CloseTrigger>
              <Dialog.Body
                p={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleCloseZoom}
              >
                <Image
                  src={zoomMedia?.src}
                  alt={zoomMedia?.alt || ''}
                  maxW="90vw"
                  maxH="90vh"
                  objectFit="contain"
                  borderRadius="lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
